import { useQuery } from "@tanstack/react-query"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Link } from "@tanstack/react-router"
import { ArrowUpRight, ExternalLink } from "lucide-react"
import { useTranslation } from "react-i18next"
import { knowledgeSuggestionsQuery } from "../lib/knowledgeSuggestionsQuery"
import { SuggestionAnalysis } from "./KnowledgeSuggestions"

type Props = {
  gapId: string
  onClose: () => void
}

export function KnowledgeSuggestionEvidenceDialog({ gapId, onClose }: Props) {
  const { t } = useTranslation()
  const { data } = useQuery(knowledgeSuggestionsQuery())
  const suggestion = data?.find(({ id }) => id === gapId)
  const sources = suggestion?.result?.proposal?.sources ?? []

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="2xl" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("knowledge.suggestions.evidence.title")}</DialogTitle>
          <DialogDescription>{t("knowledge.suggestions.evidence.description")}</DialogDescription>
        </DialogHeader>
        {suggestion && (
          <div className="space-y-4">
            <p className="text-sm font-medium">{suggestion.question}</p>
            {sources.map((source, index) => (
              <blockquote
                key={`${source.ticketKind}:${source.externalMessageId}:${index}`}
                className="space-y-2 border-l-2 border-border pl-3 text-sm"
              >
                <p className="whitespace-pre-wrap text-muted-foreground">{source.excerpt}</p>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary underline underline-offset-4"
                >
                  {t("knowledge.suggestions.ticket", {
                    id: source.externalTicketId,
                    integration: source.ticketKind,
                  })}
                  <ExternalLink aria-hidden="true" className="h-3 w-3" />
                </a>
                {source.conversations.length > 0 && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {source.conversations.map((conversation) => (
                      <Link
                        key={conversation.id}
                        to="/conversations/list/$customerId"
                        params={{ customerId: conversation.customerId }}
                        search={{ conversationId: Number(conversation.id), tab: "conversations" }}
                        className="inline-flex items-center gap-1 text-primary underline underline-offset-4"
                      >
                        {t("knowledge.suggestions.conversation", { id: conversation.id })}
                        <ArrowUpRight aria-hidden="true" className="h-3 w-3" />
                      </Link>
                    ))}
                  </div>
                )}
              </blockquote>
            ))}
            <SuggestionAnalysis suggestion={suggestion} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
